import type moment from 'moment'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { defineMessages, useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import { getCashRefundCancellationTimelineDetails } from 'components/Common/CancellationPolicies/Flash/cashRefundCancellationPolicyTimelineUtils'
import {
  formatFlexibleCancellationPolicyDate,
  getFlexibleCancellationDateChangeAllowance,
  getFlexibleCancellationTimelineDetails,
} from 'components/Common/CancellationPolicies/Flash/flexibleCancellationPolicyTimelineUtils'
import { CANCELLATION_POLICIES, SEVEN_DAYS_AFTER_PURCHASE } from 'constants/cancellationPolicies'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import type { FlashCancellationOptionRow } from 'lib/offer/flashCancellationOptions'

const messages = defineMessages({
  limitedDateChangesUntilDate: {
    id: '56KPBuyi2OE3f-8qSCVZ',
    defaultMessage: 'Until {date}: {count, plural, one {# date change} other {# date changes}}',
    description: 'Date changes fact in the flash cancellation options comparison modal, with the last day changes are allowed and how many',
  },
  limitedDateChangesUntilDays: {
    id: 'fSO0IlmlNpfL6ab-6TNp',
    defaultMessage: 'Until {days, plural, one {# day} other {# days}} before check-in: {count, plural, one {# date change} other {# date changes}}',
    description: 'Date changes fact in the flash cancellation options comparison modal before a check-in date is chosen, with how many changes are allowed',
  },
  limitedDateChanges: {
    id: 'tqUUX7PmN4BX6-v8H_TC',
    defaultMessage: '{count, plural, one {# date change} other {# date changes}}',
    description: 'Date changes fact in the flash cancellation options comparison modal for an option with no refund deadline, with how many changes are allowed',
  },
  unlimitedDateChanges: {
    id: 'iuac5gfLkMDiZcn6b3PD',
    defaultMessage: 'Unlimited date changes',
    description: 'Date changes fact in the flash cancellation options comparison modal when the offer allows unlimited date changes',
  },
  noDateChanges: {
    id: 'Y9gkzx604JXkb3q2RgAg',
    defaultMessage: 'No date changes',
    description: 'Date changes fact in the flash cancellation options comparison modal when the offer does not allow date changes',
  },
  unknownDateChangesUntilDate: {
    id: 'j4MV3p-4J_ttaVL3xd_u',
    defaultMessage: 'Until {date}: date changes may be permitted',
    description: 'Date changes fact in the flash cancellation options comparison modal when the offer does not state how many changes are allowed, with the last day',
  },
  unknownDateChangesUntilDays: {
    id: 'a78dK9rmV09030e0mPKC',
    defaultMessage: 'Until {days, plural, one {# day} other {# days}} before check-in: date changes may be permitted',
    description: 'Date changes fact in the flash cancellation options comparison modal before a check-in date is chosen when the offer does not state how many changes are allowed',
  },
  unknownDateChanges: {
    id: 'C6Yihz40QvpLZLNf7sPA',
    defaultMessage: 'Date changes may be permitted',
    description: 'Date changes fact in the flash cancellation options comparison modal for an option with no refund deadline when the offer does not state how many changes are allowed',
  },
  fullRefundUntilDate: {
    id: 'hNY-HcIVkMV8w_CECWVn',
    defaultMessage: 'Until {date}: <refund>full refund</refund>',
    description: 'Change of mind fact in the flash cancellation options comparison modal, with the last day a cancellation gets a full refund',
  },
  fullRefundWithinDaysOfPurchase: {
    id: 'IVlmsZApMVu4jIu_J2c3',
    defaultMessage: 'Within {days, plural, one {# day} other {# days}} of purchase: <refund>full refund</refund>',
    description: 'Change of mind fact for the Flexible option in the flash cancellation options comparison modal before a check-in date is chosen',
  },
  fullRefundUntilDays: {
    id: 'AKVpPvoxOWs9AAfGXv1F',
    defaultMessage: 'Until {days, plural, one {# day} other {# days}} before check-in: <refund>full refund</refund>',
    description: 'Change of mind fact for the Premium Flex option in the flash cancellation options comparison modal before a check-in date is chosen',
  },
  noRefund: {
    id: 'JHtDG6KmS48z2bcfI9xL',
    defaultMessage: 'No refund',
    description: 'Change of mind and refund facts for the Non refundable option in the flash cancellation options comparison modal',
  },
  creditRefundUntilDate: {
    id: 'lVO7qBT3F68VpYaK7nuW',
    defaultMessage: 'Until {date}: <refund>credit refund</refund>',
    description: 'What you get back fact for the Flexible option in the flash cancellation options comparison modal, with the last day a cancellation is refunded as credit',
  },
  creditRefundUntilDays: {
    id: '9cxHAGE9OWGS4_dCl4qC',
    defaultMessage: 'Until {days, plural, one {# day} other {# days}} before check-in: <refund>credit refund</refund>',
    description: 'What you get back fact for the Flexible option in the flash cancellation options comparison modal before a check-in date is chosen',
  },
  refundToOriginalPayment: {
    id: 'SN1ye6fI_7Oe9aaDH-dA',
    defaultMessage: 'Refund to original payment',
    description: 'What you get back fact for the Premium Flex option in the flash cancellation options comparison modal, refunded to the card used to pay',
  },
})

export interface FlashCancellationOptionFact {
  content: React.ReactNode
  positive: boolean
}

export interface FlashCancellationOptionFacts {
  dateChanges: FlashCancellationOptionFact
  changeOfMind: FlashCancellationOptionFact
  whatYouGetBack: FlashCancellationOptionFact
}

function positive(content: React.ReactNode): FlashCancellationOptionFact {
  return { content, positive: true }
}

function negative(content: React.ReactNode): FlashCancellationOptionFact {
  return { content, positive: false }
}

interface Params {
  rows: Array<FlashCancellationOptionRow>
  checkIn?: string | moment.Moment
  propertyTimezoneOffset?: number
  numberOfDateChangesAllowed?: string
}

export default function useFlashCancellationOptionFacts(params: Params): Map<string, FlashCancellationOptionFacts> {
  const { rows, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed } = params
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const refundColour = useRebrandSuccessDarkColour()

  const refund = useCallback((chunks: React.ReactNode) => (
    <BodyText key="refund" as="span" variant="medium" weight="bold" colour={refundColour}>{chunks}</BodyText>
  ), [refundColour])

  return useMemo(() => {
    const allowance = getFlexibleCancellationDateChangeAllowance(numberOfDateChangesAllowed)

    const getDateChanges = (deadline: string | undefined, days: number | undefined): FlashCancellationOptionFact => {
      if (allowance.kind === 'unlimited') return positive(intl.formatMessage(messages.unlimitedDateChanges))
      if (allowance.kind === 'none') return negative(intl.formatMessage(messages.noDateChanges))
      if (allowance.kind === 'limited') {
        if (deadline) return positive(intl.formatMessage(messages.limitedDateChangesUntilDate, { date: deadline, count: allowance.count }))
        if (days) return positive(intl.formatMessage(messages.limitedDateChangesUntilDays, { days, count: allowance.count }))
        return positive(intl.formatMessage(messages.limitedDateChanges, { count: allowance.count }))
      }
      if (deadline) return positive(intl.formatMessage(messages.unknownDateChangesUntilDate, { date: deadline }))
      if (days) return positive(intl.formatMessage(messages.unknownDateChangesUntilDays, { days }))
      return positive(intl.formatMessage(messages.unknownDateChanges))
    }

    return new Map(rows.map((row): [string, FlashCancellationOptionFacts] => {
      if (row.policyType === CANCELLATION_POLICIES.NON_REFUNDABLE) {
        const noRefund = negative(intl.formatMessage(messages.noRefund))
        return [row.key, {
          dateChanges: getDateChanges(undefined, undefined),
          changeOfMind: noRefund,
          whatYouGetBack: noRefund,
        }]
      }

      if (row.policyType === CANCELLATION_POLICIES.CASH_REFUND_PRIOR_TO_CHECK_IN_TEN_DAYS) {
        const details = getCashRefundCancellationTimelineDetails({ checkInDate: checkIn, propertyTimezoneOffset })
        const deadline = checkIn && details.deadline
          ? formatFlexibleCancellationPolicyDate(details.deadline, details.today, intlDateFormatter)
          : undefined
        return [row.key, {
          dateChanges: getDateChanges(deadline, details.days),
          changeOfMind: positive(deadline
            ? intl.formatMessage(messages.fullRefundUntilDate, { date: deadline, refund })
            : intl.formatMessage(messages.fullRefundUntilDays, { days: details.days, refund })),
          whatYouGetBack: positive(intl.formatMessage(messages.refundToOriginalPayment)),
        }]
      }

      const details = getFlexibleCancellationTimelineDetails({
        cancellationPolicy: { type: row.policyType ?? 'flexible-cancellation-ten-days', policyDetail: [] },
        checkInDate: checkIn,
        propertyTimezoneOffset,
      })
      const deadline = checkIn && details.startBasedOnCheckIn
        ? formatFlexibleCancellationPolicyDate(details.startBasedOnCheckIn, details.today, intlDateFormatter)
        : undefined
      const changeOfMindEnd = checkIn
        ? formatFlexibleCancellationPolicyDate(details.startBasedOnToday, details.today, intlDateFormatter)
        : undefined
      return [row.key, {
        dateChanges: getDateChanges(deadline, details.days),
        changeOfMind: positive(changeOfMindEnd
          ? intl.formatMessage(messages.fullRefundUntilDate, { date: changeOfMindEnd, refund })
          : intl.formatMessage(messages.fullRefundWithinDaysOfPurchase, { days: SEVEN_DAYS_AFTER_PURCHASE, refund })),
        whatYouGetBack: positive(!checkIn
          ? intl.formatMessage(messages.creditRefundUntilDays, { days: details.days, refund })
          : details.cancellationAfterChangeOfMindPeriod && deadline
            ? intl.formatMessage(messages.creditRefundUntilDate, { date: deadline, refund })
            : intl.formatMessage(messages.refundToOriginalPayment)),
      }]
    }))
  }, [rows, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed, intl, intlDateFormatter, refund])
}
